import { GOOGLE_ADS_FRAME_PATH, GOOGLE_ADS_URL_PARAMETERS, validGoogleAdsUrlParameter, validGoogleReceipt, type GooglePurchaseReceipt } from "@/lib/google-ads-config";
export { GOOGLE_ADS_TAG_ID, GOOGLE_ADS_PURCHASE, GOOGLE_ADS_CHECKOUT } from "@/lib/google-ads-config";

let frame: HTMLIFrameElement | undefined;
let frameToken: string | undefined;
let ready = false;
let allowed = false;
let suspended = false;
let listening = false;
const sentPurchases = new Set<string>();
const pendingPurchases = new Set<string>();

function clearCookies(): void {
  for (const cookie of document.cookie.split(";")) {
    const name = cookie.split("=", 1)[0].trim();
    if (!/^_gcl_[a-zA-Z0-9_]+$/.test(name) && name !== "_gac_gb_18050981111") continue;
    for (const domain of ["", window.location.hostname, ".crimp.studio"]) {
      document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ""}`;
    }
  }
}

function discardFrame(): void {
  // Ending the browsing context stops the SDK's automatic history/form listeners
  // as well as our own conversions. A consent update alone leaves cookieless pings.
  frame?.remove();
  frame = undefined;
  frameToken = undefined;
  ready = false;
  pendingPurchases.clear();
}

function receive(event: MessageEvent): void {
  if (!allowed || !frame || event.source !== frame.contentWindow || event.origin !== "https://crimp.studio" || event.data?.token !== frameToken) return;
  if (event.data?.type === "crimp:ads:ready") ready = true;
  if (event.data?.type === "crimp:ads:purchase-sent" && typeof event.data.eventId === "string" && pendingPurchases.delete(event.data.eventId)) {
    sentPurchases.add(event.data.eventId);
    try { localStorage.setItem(`crimp:google-purchase:${event.data.eventId}`, String(Date.now())); } catch { /* Memory and Google's transaction ID still deduplicate. */ }
  }
}

/** Shared policy owns consent. The remote SDK never runs in Crimp's app DOM. */
export function syncGoogleAds(permitted: boolean, clearOnDeny = false): void {
  allowed = permitted && !suspended && window.location.origin === "https://crimp.studio";
  if (!allowed) {
    discardFrame();
    if (clearOnDeny) clearCookies();
    return;
  }
  if (frame) return;
  if (!listening) { window.addEventListener("message", receive); listening = true; }
  const source = new URL(window.location.href);
  const url = new URL(GOOGLE_ADS_FRAME_PATH, source.origin);
  // Google reads attribution from the iframe's actual URL. Forward only bounded
  // click IDs and aggregate Google source/campaign IDs, never app URLs or titles.
  for (const key of GOOGLE_ADS_URL_PARAMETERS) {
    const value = source.searchParams.get(key);
    if (value !== null && validGoogleAdsUrlParameter(key, value)) url.searchParams.set(key, value);
  }
  const next = document.createElement("iframe");
  const token = crypto.randomUUID();
  frame = next;
  frameToken = token;
  next.id = "crimp-google-ads";
  next.title = "Advertising measurement";
  next.hidden = true;
  next.tabIndex = -1;
  next.setAttribute("aria-hidden", "true");
  next.setAttribute("sandbox", "allow-scripts allow-same-origin");
  next.referrerPolicy = "no-referrer";
  next.src = url.href;
  next.onload = () => {
    if (allowed && frame === next && frameToken === token) next.contentWindow?.postMessage({ type: "crimp:ads:init", token }, source.origin);
  };
  (document.body ?? document.documentElement).appendChild(next);
}

/** Destroy the SDK BEFORE the private URL reaches browser history listeners. */
export function suspendGoogleAds(): void { suspended = true; allowed = false; discardFrame(); }

export function trackGoogleCheckout(): boolean {
  if (!allowed || suspended || !ready || !frame) return false;
  frame.contentWindow?.postMessage({ type: "crimp:ads:checkout", token: frameToken }, "https://crimp.studio");
  return true;
}

/** Only the authenticated server receipt is accepted; no client checkout totals. */
export function trackVerifiedGooglePurchase(receipt: GooglePurchaseReceipt): boolean {
  if (!validGoogleReceipt(receipt) || !allowed || suspended || !ready || !frame) return false;
  if (sentPurchases.has(receipt.eventId)) return true;
  try { const sentAt = Number(localStorage.getItem(`crimp:google-purchase:${receipt.eventId}`)); if (sentAt > 0 && Date.now() - sentAt < 172_800_000) return true; } catch { /* Browser storage is optional. */ }
  if (!pendingPurchases.has(receipt.eventId)) {
    pendingPurchases.add(receipt.eventId);
    frame.contentWindow?.postMessage({ type: "crimp:ads:purchase", token: frameToken, receipt: { eventId: receipt.eventId, value: receipt.value, currency: receipt.currency } }, "https://crimp.studio");
  }
  // The verification poll checks again after the frame acknowledges dispatch.
  return false;
}
